import { defineStore } from 'pinia';
import { basculerDemo } from '@/lib/modeDemo';
import { chapitreParId, chapitresPour, personasPour, type Chapitre, type Etape } from '@/components/decouverte/chapitres';
import { PERSONAS, type PersonaId } from '@/demo/monde';
import { ACCOUNT_KINDS, isRole, type AccountKind, type Role } from '@its/shared';

/**
 * MODE DÉCOUVERTE (DEC-025) : le mode démo (données fictives, servies par le
 * navigateur) et la visite guidée qui s'y promène — le même couple que dans
 * Plans d'actions et l'outil IGD, pour que les outils du groupe s'apprennent
 * de la même façon.
 *
 * Ce store pilote :
 *  (a) l'interrupteur lu par le client d'API (`lib/modeDemo`) ;
 *  (b) le rôle ENDOSSÉ dans la démo (cliente, intervenant, facturation, admin) ;
 *  (c) l'état de la visite : chapitre, étape, progression.
 *
 * Persistance : localStorage `bt.decouverte`. Un rechargement en pleine démo
 * reste en démo, sur le même rôle, avec les chapitres déjà terminés.
 */

interface Persiste {
  actif: boolean;
  persona: PersonaId;
  termines: string[];
  rolesReels: Role[] | null;
  natureReelle: AccountKind | null;
}

interface State {
  actif: boolean;
  persona: PersonaId;
  chapitreId: string | null;
  etape: number;
  termines: string[];
  /**
   * Les rôles de la VRAIE session, retenus à l'entrée en démo : une fois dedans,
   * l'identité est celle du personnage endossé, et c'est sur la personne réelle
   * que le sommaire filtre ses chapitres. `null` = entrée sans compte.
   */
  rolesReels: Role[] | null;
  /**
   * La NATURE de la vraie session (staff, collaborateur, client), retenue avec
   * ses rôles : elle décide si la visite de l'équipe est montrée. `null` =
   * entrée sans compte, traitée comme un client (`voitLaVisiteDeLEquipe`).
   */
  natureReelle: AccountKind | null;
  /**
   * Incrémentée à chaque changement de monde (entrée, sortie, rôle, remise à
   * zéro) : la coquille s'en sert comme clé pour REMONTER la page courante,
   * qui recharge alors ses données au lieu d'afficher celles d'avant.
   */
  generation: number;
}

const CLE = 'bt.decouverte';

export const useDecouverteStore = defineStore('decouverte', {
  state: (): State => ({
    actif: false,
    persona: 'demandeur',
    chapitreId: null,
    etape: 0,
    termines: [],
    rolesReels: null,
    natureReelle: null,
    generation: 0,
  }),
  getters: {
    chapitre: (s): Chapitre | null => (s.chapitreId ? chapitreParId(s.chapitreId) : null),
    etapeCourante(s): Etape | null {
      const ch = s.chapitreId ? chapitreParId(s.chapitreId) : null;
      return ch?.etapes[s.etape] ?? null;
    },
    enVisite: (s): boolean => s.actif && s.chapitreId !== null,
    estTermine: (s) => (id: string): boolean => s.termines.includes(id),
    /** Part des chapitres PROPOSÉS déjà terminés : un chapitre qui ne vous concerne pas ne compte pas. */
    progressionSur: (s) => (proposes: readonly { id: string }[]): number =>
      proposes.length ? Math.round((proposes.filter((c) => s.termines.includes(c.id)).length / proposes.length) * 100) : 0,
  },
  actions: {
    /** Relit l'état persisté — AVANT le premier appel d'API (main.ts). */
    hydrater(): void {
      try {
        const brut = localStorage.getItem(CLE);
        if (brut) {
          const p = JSON.parse(brut) as Partial<Persiste>;
          this.actif = p.actif === true;
          if (p.persona && (PERSONAS as readonly string[]).includes(p.persona)) this.persona = p.persona;
          this.termines = Array.isArray(p.termines) ? p.termines.filter((t) => typeof t === 'string') : [];
          this.rolesReels = Array.isArray(p.rolesReels) ? p.rolesReels.filter(isRole) : null;
          // Une progression d'avant le cloisonnement n'a pas de nature : on la tient pour client.
          this.natureReelle = (ACCOUNT_KINDS as readonly string[]).includes(p.natureReelle as string) ? (p.natureReelle as AccountKind) : null;
          if (!personasPour(this.natureReelle).includes(this.persona)) this.persona = 'demandeur';
        }
      } catch {
        /* stockage indisponible : on démarre hors démo */
      }
      basculerDemo(this.actif);
      if (this.actif) void this.brancherPersona();
    },

    persister(): void {
      try {
        const p: Persiste = {
          actif: this.actif,
          persona: this.persona,
          termines: this.termines,
          rolesReels: this.rolesReels,
          natureReelle: this.natureReelle,
        };
        localStorage.setItem(CLE, JSON.stringify(p));
      } catch {
        /* la progression n'est pas critique */
      }
    },

    /** Informe le serveur de démo du rôle endossé (chargé à la demande). */
    async brancherPersona(): Promise<void> {
      const { endosser } = await import('@/demo/serveur');
      endosser(this.persona);
    },

    /**
     * Entre en démo, sur un rôle donné. `rolesReels` = les rôles de la session
     * qu'on quitte (null sans compte). L'identité est relue par l'appelant (`useModeDemo`).
     */
    async entrer(persona: PersonaId | undefined, rolesReels: Role[] | null, natureReelle: AccountKind | null): Promise<void> {
      this.rolesReels = rolesReels;
      this.natureReelle = natureReelle;
      if (persona) this.persona = persona;
      // Un client n'endosse jamais un rôle de l'équipe, quel que soit l'appelant.
      if (!personasPour(natureReelle).includes(this.persona)) this.persona = 'demandeur';
      this.actif = true;
      basculerDemo(true);
      await this.brancherPersona();
      this.generation += 1;
      this.persister();
    },

    async changerPersona(persona: PersonaId): Promise<void> {
      if (!personasPour(this.natureReelle).includes(persona)) return;
      this.persona = persona;
      await this.brancherPersona();
      this.generation += 1;
      this.persister();
    },

    quitter(): void {
      this.actif = false;
      this.rolesReels = null;
      this.natureReelle = null;
      this.chapitreId = null;
      this.etape = 0;
      basculerDemo(false);
      this.generation += 1;
      this.persister();
    },

    async reinitialiser(): Promise<void> {
      const { reinitialiserDemo } = await import('@/demo/serveur');
      reinitialiserDemo();
      await this.brancherPersona();
      this.chapitreId = null;
      this.etape = 0;
      this.generation += 1;
    },

    /* ---------------- Visite ---------------- */

    lancerChapitre(id: string): void {
      // Le cloisonnement vaut ici aussi : un chapitre de l'équipe ne s'ouvre pas pour un client, d'où qu'on l'appelle.
      if (!chapitresPour(this.rolesReels, this.natureReelle).some((c) => c.id === id)) return;
      this.chapitreId = id;
      this.etape = 0;
    },

    fermerVisite(): void {
      this.chapitreId = null;
      this.etape = 0;
    },

    suivante(): void {
      const ch = this.chapitre;
      if (!ch) return;
      if (this.etape >= ch.etapes.length - 1) {
        if (!this.termines.includes(ch.id)) this.termines.push(ch.id);
        this.persister();
        this.fermerVisite();
      } else {
        this.etape += 1;
      }
    },

    precedente(): void {
      if (this.etape > 0) this.etape -= 1;
    },

    oublierProgression(): void {
      this.termines = [];
      this.persister();
    },
  },
});
